import { DateTime, Interval } from 'luxon';
import { AggregationLevel, ComparisonType, SensorType, SensorTypeToSentinelType, SentinelParameterKey, SentinelType, SentinelTypeToThresholdLineType, SentinelTypeToSensorType, } from '../types';
import { formatLargeNumber, mapArrayToObject, deriveDateValue, toFahrenheit, TIME_IN_MILLISECONDS, KWH_UNIT, } from '.';
import mergeDeepRight from 'ramda/src/mergeDeepRight';
import compareAsc from 'date-fns/compareAsc';
import { TemperatureUnit } from '../types/Global';
import { getUserPreferenceTemperatureUnit } from './temperature';
export const DefaultChartStyles = {
    tick: {
        fontSize: '0.625rem',
        reportFontSize: '1rem',
        fill: 'var(--ambient-basePlus50)',
    },
    legendContainer: {
        textAlign: 'left',
        paddingTop: '1.5rem',
        fontSize: '0.75rem',
        reportFontSize: '1.25rem',
        color: 'var(--ambient-base)',
    },
    axisLineColor: 'var(--ambient-basePlus90)',
    chartHeight: 250,
    chartCompressedHeight: 200,
    yAxisWidth: 20,
    throttleDelay: 125,
};
export const generateXTicks = (fromDate, toDate, options) => {
    const { labelEveryNthTick = 2, labelEveryNthTickThreshold = 14, ianaTimeZone, } = options || {};
    const durationInHours = DateTime.fromMillis(toDate)
        .diff(DateTime.fromMillis(fromDate), 'hours')
        .toObject().hours;
    const durationInDays = (durationInHours || 0) / 24;
    if (durationInDays <= 2) {
        return generateHourlyXTicks(fromDate, toDate, labelEveryNthTick, labelEveryNthTickThreshold);
    }
    else {
        return generateDailyXTicks(fromDate, toDate, labelEveryNthTick, labelEveryNthTickThreshold, ianaTimeZone);
    }
};
export function generateDailyXTicks(fromDate, toDate, labelEveryNthTick, labelEveryNthTickThreshold, ianaTimeZone) {
    let xTicks = [];
    const fromDateTime = DateTime.fromMillis(fromDate).setZone(ianaTimeZone);
    const toDateTime = DateTime.fromMillis(toDate).setZone(ianaTimeZone);
    const intervalDateTime = Interval.fromDateTimes(fromDateTime, toDateTime);
    const daysBetween = intervalDateTime
        .splitBy({ days: 1 })
        //we want xTicks to display at noon of site timeZone
        .map((d) => d.start.startOf('day').set({ hour: 12 }))
        .filter((d) => d >= fromDateTime && d <= toDateTime);
    if (daysBetween.length >= labelEveryNthTickThreshold) {
        for (let i = 1; i < daysBetween.length; i += labelEveryNthTick) {
            xTicks.push(daysBetween[i].toMillis());
        }
    }
    else {
        xTicks = daysBetween.map((dateTime) => {
            return dateTime.toMillis();
        });
    }
    return xTicks;
}
export function generateHourlyXTicks(fromDate, toDate, labelEveryNthTick, labelEveryNthTickThreshold) {
    const fromDateTime = DateTime.fromMillis(fromDate);
    const toDateTime = DateTime.fromMillis(toDate);
    const intervalDateTime = Interval.fromDateTimes(fromDateTime, toDateTime);
    const hoursBetween = intervalDateTime
        .splitBy({ hours: 1 })
        .map((d) => d.start.startOf('hour'))
        .filter((d) => d >= fromDateTime && d <= toDateTime);
    const recursivelyDetermineXTicks = (hoursBetween, labelEveryNthTick, labelEveryNthTickThreshold) => {
        if (hoursBetween.length >= labelEveryNthTickThreshold) {
            const dates = [];
            for (let i = 1; i < hoursBetween.length; i += labelEveryNthTick) {
                dates.push(hoursBetween[i]);
            }
            return recursivelyDetermineXTicks(dates, labelEveryNthTick, labelEveryNthTickThreshold);
        }
        else {
            return hoursBetween
                .filter((t) => t >= fromDateTime && t <= toDateTime)
                .map((date) => date.toMillis());
        }
    };
    return recursivelyDetermineXTicks(hoursBetween, labelEveryNthTick, labelEveryNthTickThreshold);
}
// check whether user dragged cursor right to left or vice versa
export const determineDateRangeAfterZoomIn = (refAreaStart, refAreaEnd) => {
    return {
        fromDate: refAreaEnd > refAreaStart ? refAreaStart : refAreaEnd,
        toDate: refAreaEnd > refAreaStart ? refAreaEnd : refAreaStart,
    };
};
export const determineXTicksAfterZoomIn = (refAreaStart, refAreaEnd, options = {}) => {
    return refAreaEnd > refAreaStart
        ? generateXTicks(refAreaStart, refAreaEnd, options)
        : generateXTicks(refAreaEnd, refAreaStart, options);
};
export const getChartDomain = (min, max) => {
    const minTick = Math.floor(min > 0 ? min * 0.97 : min * 1.03);
    const maxTick = Math.ceil(max > 0 ? max * 1.03 : max * 0.97);
    return [minTick, maxTick];
};
export const calculateYTicks = (min, max) => {
    const [minTick, maxTick] = getChartDomain(min, max);
    const range = maxTick - minTick;
    return [
        minTick,
        minTick + range / 4,
        minTick + range / 2,
        minTick + (3 * range) / 4,
        maxTick,
    ];
};
export function chartConfig(s, currentUser) {
    switch (s) {
        case SensorType.TEMPERATURE:
            const unit = getUserPreferenceTemperatureUnit(currentUser);
            return {
                title: 'Temperature',
                apiSensorType: 'Temperature',
                samplesType: 'temperature',
                unit,
                valueFormatterFn: (v) => Math.round(v * 10) / 10,
                tooltipFormatterFn: (v) => `${Math.round(v * 10) / 10} ${unit}`,
                type: 'monotone',
            };
        case SensorType.BINARY:
            return {
                title: 'Binary',
                apiSensorType: 'Binary',
                unit: '',
                samplesType: 'binary',
                valueFormatterFn: (v) => (v.includes(true) ? 1 : 0),
                tooltipFormatterFn: (v) => (v ? 'Opened' : 'Closed'),
                type: 'stepAfter',
            };
        case SensorType.HUMIDITY:
            return {
                title: 'Humidity',
                apiSensorType: 'Humidity',
                samplesType: 'humidity',
                valueFormatterFn: (v) => v,
                tooltipFormatterFn: (v) => `${v} %`,
                unit: '%',
                type: 'monotone',
            };
        default:
            throw new Error(`Unknown Sensor Type: ${s}`);
    }
}
export function getChartConfig(sensorTypes, currentUser) {
    const config = {};
    sensorTypes.forEach((t) => {
        config[t] = chartConfig(t, currentUser);
    });
    return config;
}
export const processSamplesData = (response, currentUser) => {
    const data = new Map();
    response.forEach((sample) => {
        const timestamp = sample.timestamp * 1000;
        const timestampKey = timestamp.toString();
        const type = sample.type.toLowerCase();
        const value = processSamplesDataValue(sample, currentUser);
        if (data.has(timestampKey)) {
            data.set(timestampKey, Object.assign(Object.assign({}, data.get(timestampKey)), { [type]: value }));
        }
        else {
            data.set(timestampKey, { [type]: value, timestamp });
        }
    });
    return fillInDataGapsWithNullPoints([...data.values()]);
};
const processSamplesDataValue = (sample, currentUser) => {
    if (sample.type === 'BINARY') {
        if (sample.value === true) {
            return 1;
        }
        else
            return 0;
    }
    if (sample.type === 'TEMPERATURE') {
        const temperatureUnit = getUserPreferenceTemperatureUnit(currentUser);
        switch (temperatureUnit) {
            case TemperatureUnit.C:
                return sample.value;
            case TemperatureUnit.F:
                return toFahrenheit(sample.value);
        }
    }
    return sample.value;
};
export const processThresholdLines = (sentinels, currentUser, acceptedSentinelTypes) => {
    const temperatureUnit = getUserPreferenceTemperatureUnit(currentUser);
    const determineParameterValue = (sentinel, key) => {
        const { sentinelType, parameters } = sentinel;
        const value = parameters[key];
        switch (sentinelType) {
            case SentinelType.TEMPERATURE_AND_DOOR:
            case SentinelType.TEMPERATURE:
                return temperatureUnit === TemperatureUnit.C
                    ? value
                    : toFahrenheit(value);
            case SentinelType.BINARY:
                return value === 'OPEN' ? 1 : 0;
            default:
                return value;
        }
    };
    const determineSentinelParameterKeys = (acceptedSentinelTypes, comparisonType) => {
        const tempRelatedSentinelTypes = [
            SentinelType.TEMPERATURE,
            SentinelType.TEMPERATURE_AND_DOOR,
        ];
        const isTempRelatedSentinel = tempRelatedSentinelTypes.some((type) => acceptedSentinelTypes.includes(type));
        const isActivePowerThresholdSentinel = acceptedSentinelTypes.includes(SentinelType.ACTIVE_POWER_THRESHOLD);
        const isBasicDoorSwitchSentinel = acceptedSentinelTypes.includes(SentinelType.BINARY);
        if (isTempRelatedSentinel || isActivePowerThresholdSentinel) {
            switch (comparisonType) {
                case ComparisonType.inner:
                case ComparisonType.outer:
                    return ['rangeLowerBound', 'rangeUpperBound'];
                case ComparisonType.eq:
                case ComparisonType.gt:
                case ComparisonType.lt:
                    return ['threshold'];
                default:
                    return ['threshold'];
            }
        }
        else if (isBasicDoorSwitchSentinel) {
            return ['openClosed'];
        }
        return [];
    };
    const processSentinelParameters = (s, filteredSentinelParameterKeys) => {
        return filteredSentinelParameterKeys.map((key) => ({
            title: `${s.id} ${s.title} ${SentinelParameterKey[key]}`,
            value: determineParameterValue(s, key),
            type: SentinelTypeToThresholdLineType[s.sentinelType],
        }));
    };
    const filteredSentinels = sentinels.filter((s) => acceptedSentinelTypes.indexOf(s.sentinelType) > -1);
    return filteredSentinels
        .map((s) => {
        const filteredSentinelParameterKeys = Object.keys(s.parameters).filter((key) => determineSentinelParameterKeys(acceptedSentinelTypes, s.parameters.comparisonType).indexOf(key) > -1);
        return processSentinelParameters(s, filteredSentinelParameterKeys);
    })
        .reduce((a, b) => a.concat(b), []);
};
export const convertSensorTypesToSentinelTypes = (sensorTypes) => {
    return sensorTypes
        .map((type) => SensorTypeToSentinelType[type])
        .reduce((a, b) => a.concat(b), []);
};
export function createChartResolutionAggregationFilter() {
    return [
        {
            title: '1 Minute',
            aggregationLevel: AggregationLevel.ONE_MINUTE,
        },
        {
            title: '15 Minutes',
            aggregationLevel: AggregationLevel.FIFTEEN_MINUTE,
        },
        {
            title: '1 Hour',
            aggregationLevel: AggregationLevel.ONE_HOUR,
        },
        {
            title: '1 Day',
            aggregationLevel: AggregationLevel.ONE_DAY,
        },
    ];
}
const ChartColorSet = [
    {
        lineStroke: 'var(--turquoise-base)',
        textOnDark: 'var(--turquoise-basePlus25)',
    },
    {
        lineStroke: 'var(--indigo-base)',
        textOnDark: 'var(--indigo-basePlus50)',
    },
    {
        lineStroke: 'var(--lemon-baseMinus25)',
        textOnDark: 'var(--lemon-baseMinus10)',
    },
    {
        lineStroke: 'var(--pink-baseMinus25)',
        textOnDark: 'var(--pink-basePlus25)',
    },
    {
        lineStroke: 'var(--sun-base)',
        textOnDark: 'var(--sun-basePlus25)',
    },
    {
        lineStroke: 'var(--purple-bright)',
        textOnDark: 'var(--purple-basePlus25)',
    },
    {
        lineStroke: 'var(--orange-base)',
        textOnDark: 'var(--orange-basePlus25)',
    },
    {
        lineStroke: 'var(--blue-baseMinus75)',
        textOnDark: 'var(--darkBlue-text)',
    },
];
export const ChartColorToTooltipColor = (() => {
    const chartColorToTooltipObject = {};
    ChartColorSet.forEach((chartColor) => {
        chartColorToTooltipObject[chartColor.lineStroke] = chartColor.textOnDark;
    });
    return chartColorToTooltipObject;
})();
/**
 * Ananya's design specifies that the last color in the palette is always the dark blue (not )
 * @param numColors number of colors one will need to choose from for the chart etc
 */
export const generateChartColorPalette = (numColors) => {
    if (numColors === 0) {
        return [];
    }
    return [
        ...MultiLineChartColorSet.slice(0, numColors - 1),
        MultiLineChartColorSet[MultiLineChartColorSet.length - 1],
    ];
};
const MultiLineChartColorSet = ChartColorSet.map((item, index) => (Object.assign(Object.assign({}, item), { order: index })));
export const orderColorSetForChart = (sensors, assignedChartColors) => {
    // If we don't have assignments for all the colors yet return []
    if (Object.entries(assignedChartColors).length !== sensors.length)
        return [];
    return sensors.map(({ id }) => assignedChartColors[id] !== undefined
        ? assignedChartColors[id]
        : { lineStroke: '', textOnDark: '', order: 999 });
};
export const processFlatNotificationsForChart = (flatNotifications, currentUser) => {
    return flatNotifications.map((n) => ({
        at: n.at,
        priorityLevel: n.priorityLevel,
        name: n.name,
        message: n.message,
        timestamp: deriveDateValue(n.at),
        type: SentinelTypeToSensorType[n.sentinelType],
        notificationValue: convertFlatNotification(n.sentinelType, n.value, currentUser),
    }));
};
export const processNotificationsByTypes = (flatNotifications, currentUser) => {
    const notificationsByType = {};
    const processedNotifications = processFlatNotificationsForChart(flatNotifications, currentUser);
    processedNotifications.forEach((n) => {
        if (notificationsByType[n.type]) {
            notificationsByType[n.type] = [...notificationsByType[n.type], n];
        }
        else {
            notificationsByType[n.type] = [n];
        }
    });
    return notificationsByType;
};
export function convertFlatNotification(sentinelType, flatNotificationValue, currentUser) {
    const value = flatNotificationValue === null || flatNotificationValue === void 0 ? void 0 : flatNotificationValue[0];
    if (!value) {
        return null;
    }
    const temperatureUnit = getUserPreferenceTemperatureUnit(currentUser);
    switch (sentinelType) {
        case SentinelType.BINARY:
        case SentinelType.BINARY_COUNTER:
            //API returns multiple types for this value
            return value === 'OPEN' || value === 'true' || !isNaN(Number(value))
                ? 1
                : 0;
        case SentinelType.HUMIDITY:
            // @ts-ignore todo: ts-fix
            return parseFloat(value);
        case SentinelType.TEMPERATURE: {
            // @ts-ignore todo: ts-fix
            const v = parseFloat(value);
            return temperatureUnit === TemperatureUnit.C ? v : toFahrenheit(v);
        }
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            return +value;
        default:
            return value;
    }
}
export function sortAndPossiblySliceConsumptionData(unsortedData, maxNumberCategories) {
    let data = unsortedData.sort((a, b) => b.count - a.count);
    if (data.length > maxNumberCategories) {
        const otherData = data.slice(7);
        let othersTotal = 0;
        otherData.forEach((_data) => {
            othersTotal = othersTotal + _data.count;
        });
        const others = {
            type: 'Others',
            label: 'Others',
            count: othersTotal,
            unit: KWH_UNIT,
        };
        data = data.slice(0, 7);
        data.push(others);
    }
    return data;
}
export const addNullPointsBetweenTimestampGaps = (data, valuePropertyName, timestampGap = TIME_IN_MILLISECONDS.THIRTY_MINUTES) => {
    const processedData = [];
    if (!data)
        return processedData;
    data.forEach((d, index, arr) => {
        processedData.push(d);
        const currentTimestamp = d.timestamp;
        const nextTimestamp = (arr[index + 1] || {}).timestamp;
        const doesNextTimestampExist = nextTimestamp !== undefined;
        const gapBetweenCurrentAndNextTimestamp = (nextTimestamp || 0) - currentTimestamp;
        if (doesNextTimestampExist &&
            gapBetweenCurrentAndNextTimestamp > timestampGap) {
            processedData.push({
                timestamp: currentTimestamp + 1,
                [valuePropertyName]: null,
            });
        }
    });
    return processedData;
};
export const getYTicks = (min, max) => {
    const [minTick, maxTick] = getChartDomain(min, max);
    const range = maxTick - minTick;
    return [
        minTick,
        minTick + range / 4,
        minTick + range / 2,
        minTick + (3 * range) / 4,
        maxTick,
    ];
};
export const genericYAxisTickFormatter = (tickItem) => {
    return formatLargeNumber(tickItem);
};
//*** Chart data logic ***///
export const mergeTimeSeriesData = (a = {}, b = {}) => {
    // for each dataset
    // when a gap between timestamp is above a cerntain threshold
    // fill mapping with null data
    const dataSetA = fillInDataGapsWithNullPoints(Array.isArray(a) ? a : Object.values(a));
    const dataSetB = fillInDataGapsWithNullPoints(Array.isArray(b) ? b : Object.values(b));
    // map the different data to timestamp value
    const timestampMappingA = mapArrayToObject(dataSetA, 'timestamp');
    const timestampMappingB = mapArrayToObject(dataSetB, 'timestamp');
    // merge the mapping objects then flatten resulting object into a combined array
    const preprocessedMergedData = Object.values(mergeDeepRight(timestampMappingA, timestampMappingB)).sort((a, b) => compareAsc(a.timestamp, b.timestamp));
    // result array
    const processedMergedData = [];
    preprocessedMergedData.forEach((currentDataPoint) => {
        const prevDataPoint = processedMergedData.length === 0
            ? {}
            : processedMergedData[processedMergedData.length - 1];
        processedMergedData.push(mergePrevAndCurrentDataPoint(prevDataPoint, currentDataPoint));
    });
    return processedMergedData;
};
export const fillInDataGapsWithNullPoints = (data) => {
    // Default interpolation Threshold for Time Series
    const processedData = [];
    data
        .sort((a, b) => compareAsc(a.timestamp, b.timestamp))
        .forEach((currentData, index, dataArray) => {
        var _a;
        const currentTimestamp = currentData === null || currentData === void 0 ? void 0 : currentData.timestamp;
        const nextTimestamp = (_a = dataArray[index + 1]) === null || _a === void 0 ? void 0 : _a.timestamp;
        processedData.push(currentData);
        if (
        // if timestamp is greather than threshold, insert data gap
        +nextTimestamp - +currentTimestamp >
            TIME_IN_MILLISECONDS.THIRTY_MINUTES ||
            // append a null plug at end of time series
            // prevent interpolating fake data when merging different time series data
            index === dataArray.length - 1) {
            let nullDataPoint = {};
            const keys = Object.keys(currentData);
            keys.forEach((k) => {
                nullDataPoint[k] = null;
            });
            processedData.push(Object.assign(Object.assign({}, nullDataPoint), { timestamp: currentTimestamp + 1000 }));
        }
    });
    return processedData;
};
export const partitionTimeSeriesWithGaps = (timeSeries, threshold) => {
    const segmentsList = [];
    let dataSegment = [];
    timeSeries.forEach((currentData, index, dataArray) => {
        if (index + 1 >= dataArray.length) {
            segmentsList.push(dataSegment);
            return;
        }
        const currentTimestamp = currentData.timestamp;
        const nextTimestamp = dataArray[index + 1].timestamp;
        dataSegment.push(currentData);
        if (nextTimestamp - currentTimestamp > threshold) {
            segmentsList.push(dataSegment);
            dataSegment = [];
        }
    });
    return segmentsList;
};
export const mergePrevAndCurrentDataPoint = (prevDataPoint, currentDataPoint) => {
    return mergeDeepRight(prevDataPoint, currentDataPoint);
};
//*** End chart data logic ***///
const mobileChartLabelOptions = {
    labelEveryNthTick: 4,
    labelEveryNthTickThreshold: 8,
};
const getAppliedOptions = (isMobile, options) => isMobile
    ? Object.assign(Object.assign({}, mobileChartLabelOptions), options) : options;
export const generateResponsiveXTicks = ({ fromDate, toDate, options = {}, isMobile = false, }) => {
    const appliedOptions = getAppliedOptions(isMobile, options);
    return generateXTicks(fromDate, toDate, appliedOptions);
};
export const determineResponsiveXTicksAfterZoomIn = ({ fromDate, toDate, options = {}, isMobile = false, }) => {
    const appliedOptions = getAppliedOptions(isMobile, options);
    return determineXTicksAfterZoomIn(fromDate, toDate, appliedOptions);
};
